
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {

  ArrowLeft,
  BadgeCheck,
  Building2,
  CheckCircle2,

  CreditCard,
  Edit3,

  Eye,
  LoaderCircle,
  MapPin,

  Minus,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  ShoppingCart,
  Store,
  Trash2,
 
  X,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import "../../styles/stores-manager.css";

/*
 * LA ESQUINA DEL PARQUÉS
 * Centro de control de tiendas, mesas, productos y POS.
 *
 * API existente:
 * GET/POST /stores/
 * GET/PATCH/DELETE /stores/:id/
 * GET /products/
 *
 * Contratos nuevos esperados:
 * GET/POST /store-tables/
 * PATCH/DELETE /store-tables/:id/
 * POST /table-consumptions/
 *
 * Ajusta estos tres endpoints a las rutas reales de Django.
 * La venta solo se confirma cuando el backend responde correctamente.
 */

const API_BASE_URL = (
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD
    ? "https://api.laesquinadelparques.com/api"
    : "http://localhost:8000/api")
).replace(/\/+$/, "");

const API = {
  stores: "/stores/",
  tables: "/store-tables/",
  products: "/products/",
  consumptions: "/table-consumptions/",
};

const EMPTY_STORE = {
  name: "",
  description: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  city: "",
  state: "",
  country: "Colombia",
  currency: "COP",
  commission_percentage: "0",
  opening_time: "",
  closing_time: "",
  is_active: true,
  is_featured: false,
  is_verified: false,
};

const EMPTY_TABLE = {
  name: "",
  number: "",
  capacity: "4",
  status: "available",
  notes: "",
};

const TABLE_STATES = [
  { value: "available", label: "Libre" },
  { value: "occupied", label: "Ocupada" },
  { value: "reserved", label: "Reservada" },
  { value: "bill_pending", label: "Cuenta pendiente" },
  { value: "inactive", label: "Inactiva" },
];

function getToken() {
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "null");
    return auth?.access || auth?.access_token || auth?.token || null;
  } catch {
    return null;
  }
}

function normalizeList(response) {
  let data = response;

  for (let i = 0; i < 5; i += 1) {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== "object") return [];

    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.stores)) return data.stores;
    if (Array.isArray(data.tables)) return data.tables;
    if (Array.isArray(data.products)) return data.products;
    if (Array.isArray(data.data)) return data.data;

    if (data.data && typeof data.data === "object") {
      data = data.data;
      continue;
    }

    if (data.results && typeof data.results === "object") {
      data = data.results;
      continue;
    }

    break;
  }

  return [];
}

function normalizeObject(response) {
  if (!response || typeof response !== "object") return null;

  if (response.id != null) return response;
  if (response.data && typeof response.data === "object") {
    return normalizeObject(response.data);
  }
  if (response.store && typeof response.store === "object") {
    return normalizeObject(response.store);
  }

  return response;
}

function errorMessage(data, fallback) {
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;

  if (data && typeof data === "object") {
    const key = Object.keys(data).find(
      (name) => !["success", "status", "code"].includes(name)
    );

    if (key) {
      const value = data[key];

      if (Array.isArray(value)) {
        return `${key}: ${value[0]}`;
      }

      if (typeof value === "string") {
        return `${key}: ${value}`;
      }
    }
  }

  return fallback;
}

function money(value, currency = "COP") {
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: currency || "COP",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `$ ${Number(value || 0).toLocaleString("es-CO")}`;
  }
}

function mediaUrl(value) {
  if (!value) return "";
  if (typeof value === "object") {
    value = value.url || value.image || value.src || "";
  }
  if (typeof value !== "string") return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  if (value.startsWith("/")) {
    try {
      return `${new URL(API_BASE_URL).origin}${value}`;
    } catch {
      return value;
    }
  }

  return value;
}

function storeAddress(store) {
  return [
    store?.address,
    store?.city,
    store?.state,
    store?.country,
  ].filter(Boolean).join(", ");
}

function storeIdOf(item) {
  return item?.store_id ?? item?.store?.id ?? item?.store ?? null;
}

function tableStateLabel(value) {
  return TABLE_STATES.find((item) => item.value === value)?.label ||
    value ||
    "Sin estado";
}

function getProductPrice(product) {
  return Number(product?.price ?? product?.sale_price ?? 0);
}

export default function StoresManager() {
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [tables, setTables] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [tab, setTab] = useState("stores");
  const [search, setSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const [storeFormOpen, setStoreFormOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeForm, setStoreForm] = useState({ ...EMPTY_STORE });

  const [tableFormOpen, setTableFormOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableForm, setTableForm] = useState({ ...EMPTY_TABLE });

  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [customerName, setCustomerName] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      navigate("/login", { replace: true });
      throw new Error("Tu sesión no está activa. Inicia sesión nuevamente.");
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    if (
      options.body &&
      !(options.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    let response;

    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });
    } catch {
      throw new Error(
        "No se pudo conectar con Django. Revisa el backend y VITE_API_URL."
      );
    }

    if (response.status === 401) {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("authUpdated"));
      navigate("/login", { replace: true });
      throw new Error("Tu sesión expiró. Inicia sesión nuevamente.");
    }

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    if (response.status !== 204) {
      data = contentType.includes("application/json")
        ? await response.json().catch(() => null)
        : await response.text().catch(() => "");
    }

    if (!response.ok) {
      throw new Error(
        errorMessage(data, `Error ${response.status} en ${endpoint}.`)
      );
    }

    return data;
  }, [navigate]);

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiRequest(API.stores);
      const list = normalizeList(response);
      setStores(list);

      setSelectedStoreId((previous) => {
        if (previous && list.some((store) => String(store.id) === String(previous))) {
          return previous;
        }
        return list[0]?.id != null ? String(list[0].id) : "";
      });
    } catch (err) {
      setError(err.message || "No fue posible cargar las tiendas.");
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  const loadTables = useCallback(async () => {
    if (!selectedStoreId) {
      setTables([]);
      setSelectedTableId("");
      return;
    }

    try {
      const query = `?store=${encodeURIComponent(selectedStoreId)}`;
      const response = await apiRequest(`${API.tables}${query}`);
      const list = normalizeList(response).filter(
        (item) => String(storeIdOf(item)) === String(selectedStoreId)
      );

      setTables(list);
      setSelectedTableId((previous) => {
        if (previous && list.some((item) => String(item.id) === String(previous))) {
          return previous;
        }
        return "";
      });
    } catch (err) {
      setTables([]);
      setSelectedTableId("");
      setError(
        `${err.message} Comprueba que Django tenga configurado el endpoint de mesas.`
      );
    }
  }, [apiRequest, selectedStoreId]);

  const loadProducts = useCallback(async () => {
    if (!selectedStoreId) {
      setProducts([]);
      return;
    }

    try {
      const query = `?store=${encodeURIComponent(selectedStoreId)}`;
      const response = await apiRequest(`${API.products}${query}`);
      const list = normalizeList(response);

      setProducts(
        list.filter((product) => {
          const productStore = storeIdOf(product);
          return productStore == null ||
            String(productStore) === String(selectedStoreId);
        })
      );
    } catch (err) {
      setProducts([]);
      setError(
        `${err.message} Verifica los permisos y el endpoint de productos.`
      );
    }
  }, [apiRequest, selectedStoreId]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const selectedStore = useMemo(
    () => stores.find((item) => String(item.id) === String(selectedStoreId)) || null,
    [stores, selectedStoreId]
  );

  const selectedTable = useMemo(
    () => tables.find((item) => String(item.id) === String(selectedTableId)) || null,
    [tables, selectedTableId]
  );

  const filteredStores = useMemo(() => {
    const query = search.trim().toLowerCase();

    return stores.filter((store) =>
      [
        store.name,
        store.email,
        store.phone,
        store.city,
        store.state,
        store.id,
      ].filter(Boolean).join(" ").toLowerCase().includes(query)
    );
  }, [stores, search]);

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();

    return products.filter((product) => {
      const active = product.is_active !== false;
      const text = [
        product.name,
        product.sku,
        product.barcode,
        product.description,
      ].filter(Boolean).join(" ").toLowerCase();

      return active && text.includes(query);
    });
  }, [products, productSearch]);

  const totals = useMemo(() => {
    const active = stores.filter((item) => item.is_active).length;
    const occupied = tables.filter((item) => item.status === "occupied").length;
    const available = tables.filter(
      (item) => !item.status || item.status === "available"
    ).length;

    return {
      stores: stores.length,
      active,
      tables: tables.length,
      occupied,
      available,
      products: products.length,
    };
  }, [stores, tables, products]);

  const cartTotal = useMemo(
    () => cart.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.quantity),
      0
    ),
    [cart]
  );

  function clearMessages() {
    setError("");
    setNotice("");
  }

  function startCreateStore() {
    clearMessages();
    setEditingStore(null);
    setStoreForm({ ...EMPTY_STORE });
    setStoreFormOpen(true);
  }

  function startEditStore(store) {
    clearMessages();
    setEditingStore(store);
    setStoreForm({
      ...EMPTY_STORE,
      ...store,
      commission_percentage: String(store.commission_percentage ?? 0),
      opening_time: store.opening_time?.slice(0, 5) || "",
      closing_time: store.closing_time?.slice(0, 5) || "",
    });
    setStoreFormOpen(true);
  }

  async function saveStore(event) {
    event.preventDefault();
    clearMessages();

    if (!storeForm.name.trim()) {
      setError("El nombre de la tienda es obligatorio.");
      return;
    }

    const commission = Number(storeForm.commission_percentage);

    if (!Number.isFinite(commission) || commission < 0 || commission > 100) {
      setError("La comisión debe estar entre 0 y 100.");
      return;
    }

    setBusy(true);

    try {
      const payload = {
        ...storeForm,
        name: storeForm.name.trim(),
        commission_percentage: commission,
      };

      const endpoint = editingStore
        ? `${API.stores}${editingStore.id}/`
        : API.stores;

      const response = await apiRequest(endpoint, {
        method: editingStore ? "PATCH" : "POST",
        body: JSON.stringify(payload),
      });

      const saved = normalizeObject(response);

      setStoreFormOpen(false);
      setEditingStore(null);
      await loadStores();

      if (saved?.id) setSelectedStoreId(String(saved.id));

      setNotice(
        editingStore
          ? "Tienda actualizada correctamente."
          : "Tienda creada correctamente."
      );
    } catch (err) {
      setError(err.message || "No fue posible guardar la tienda.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteStore(store) {
    if (!store?.id || busy) return;

    const confirmed = window.confirm(
      `¿Eliminar la tienda "${store.name}"?\n\nEsta acción puede fallar si existen mesas, productos, pedidos o ventas relacionados.`
    );

    if (!confirmed) return;

    clearMessages();
    setBusy(true);

    try {
      await apiRequest(`${API.stores}${store.id}/`, { method: "DELETE" });

      if (String(selectedStoreId) === String(store.id)) {
        setSelectedStoreId("");
        setSelectedTableId("");
        setCart([]);
      }

      await loadStores();
      setNotice("La tienda fue eliminada por el backend.");
    } catch (err) {
      setError(
        `${err.message} Si tiene ventas o productos asociados, Django puede exigir desactivarla en lugar de eliminarla.`
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleStoreActive(store) {
    clearMessages();
    setBusy(true);

    try {
      await apiRequest(`${API.stores}${store.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !store.is_active }),
      });

      await loadStores();
      setNotice(
        store.is_active ? "Tienda desactivada." : "Tienda activada."
      );
    } catch (err) {
      setError(err.message || "No se pudo cambiar el estado.");
    } finally {
      setBusy(false);
    }
  }

  function startCreateTable() {
    if (!selectedStoreId) {
      setError("Selecciona primero una tienda.");
      return;
    }

    clearMessages();
    setEditingTable(null);
    setTableForm({ ...EMPTY_TABLE });
    setTableFormOpen(true);
  }

  function startEditTable(table) {
    clearMessages();
    setEditingTable(table);
    setTableForm({
      ...EMPTY_TABLE,
      ...table,
      number: String(table.number ?? ""),
      capacity: String(table.capacity ?? 4),
    });
    setTableFormOpen(true);
  }

  async function saveTable(event) {
    event.preventDefault();
    clearMessages();

    if (!selectedStoreId) {
      setError("Selecciona una tienda.");
      return;
    }

    if (!tableForm.name.trim() && !tableForm.number.trim()) {
      setError("Escribe el nombre o número de la mesa.");
      return;
    }

    setBusy(true);

    try {
      const payload = {
        ...tableForm,
        store: Number(selectedStoreId),
        name: tableForm.name.trim() ||
          `Mesa ${tableForm.number}`,
        number: tableForm.number || null,
        capacity: Math.max(1, Number(tableForm.capacity) || 4),
      };

      await apiRequest(
        editingTable
          ? `${API.tables}${editingTable.id}/`
          : API.tables,
        {
          method: editingTable ? "PATCH" : "POST",
          body: JSON.stringify(payload),
        }
      );

      setTableFormOpen(false);
      setEditingTable(null);
      await loadTables();
      setNotice(editingTable ? "Mesa actualizada." : "Mesa creada.");
    } catch (err) {
      setError(
        `${err.message} Comprueba que el serializer de mesas acepte el campo "store".`
      );
    } finally {
      setBusy(false);
    }
  }

  async function deleteTable(table) {
    if (!table?.id || busy) return;

    if (table.status === "occupied" || table.status === "bill_pending") {
      setError("No puedes eliminar una mesa con una cuenta activa o pendiente.");
      return;
    }

    if (!window.confirm(`¿Eliminar la mesa "${table.name || table.number}"?`)) {
      return;
    }

    clearMessages();
    setBusy(true);

    try {
      await apiRequest(`${API.tables}${table.id}/`, { method: "DELETE" });
      if (String(selectedTableId) === String(table.id)) {
        setSelectedTableId("");
        setCart([]);
      }
      await loadTables();
      setNotice("Mesa eliminada.");
    } catch (err) {
      setError(err.message || "No fue posible eliminar la mesa.");
    } finally {
      setBusy(false);
    }
  }

  async function updateTableStatus(table, status) {
    clearMessages();
    setBusy(true);

    try {
      await apiRequest(`${API.tables}${table.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });

      await loadTables();
      setNotice(`Estado actualizado: ${tableStateLabel(status)}.`);
    } catch (err) {
      setError(err.message || "No fue posible actualizar la mesa.");
    } finally {
      setBusy(false);
    }
  }

  function addToCart(product) {
    if (!selectedTableId) {
      setError("Selecciona una mesa antes de agregar productos a la venta.");
      return;
    }

    if (selectedTable?.status === "inactive" ||
        selectedTable?.status === "reserved") {
      setError("La mesa está inactiva o reservada y no puede recibir una venta.");
      return;
    }

    const price = getProductPrice(product);

    if (price < 0 || !Number.isFinite(price)) {
      setError("El producto tiene un precio inválido.");
      return;
    }

    clearMessages();

    setCart((previous) => {
      const found = previous.find(
        (item) => String(item.id) === String(product.id)
      );

      if (found) {
        return previous.map((item) =>
          String(item.id) === String(product.id)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...previous,
        {
          id: product.id,
          name: product.name,
          price,
          quantity: 1,
          sku: product.sku || "",
        },
      ];
    });
  }

  function changeCartQuantity(productId, delta) {
    setCart((previous) =>
      previous
        .map((item) =>
          String(item.id) === String(productId)
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  }

  async function submitSale() {
    if (!selectedStoreId || !selectedTableId) {
      setError("Selecciona una tienda y una mesa.");
      return;
    }

    if (!cart.length) {
      setError("Agrega al menos un producto a la venta.");
      return;
    }

    if (!window.confirm(
      `¿Registrar la venta por ${money(cartTotal, selectedStore?.currency)}?`
    )) {
      return;
    }

    clearMessages();
    setBusy(true);

    try {
      /*
       * Contrato esperado de ejemplo.
       * Adapta los nombres a tu modelo/serializer real.
       * El backend debe validar precios, stock, permisos y totales.
       */
      await apiRequest(API.consumptions, {
        method: "POST",
        body: JSON.stringify({
          store: Number(selectedStoreId),
          table: Number(selectedTableId),
          customer_name: customerName.trim(),
          notes: customerNotes.trim(),
          payment_method: paymentMethod,
          items: cart.map((item) => ({
            product: item.id,
            quantity: item.quantity,
          })),
        }),
      });

      setCart([]);
      setCustomerName("");
      setCustomerNotes("");

      await Promise.all([loadTables(), loadProducts()]);

      setNotice(
        "La venta fue registrada por Django. Verifica en el backend el estado del pago y el inventario."
      );
    } catch (err) {
      setError(
        `${err.message} No se confirmó la venta. Verifica el endpoint y el serializer de consumos.`
      );
    } finally {
      setBusy(false);
    }
  }

  const tabs = [
    { id: "stores", label: "Tiendas", icon: Store },
    { id: "tables", label: "Mesas", icon: Building2 },
    { id: "products", label: "Productos", icon: ShoppingCart },
    { id: "pos", label: "Punto de venta", icon: CreditCard },
  ];

  return (
    <ProfilePageLayout
      title="Tiendas y punto de venta"
      subtitle="Administra establecimientos, mesas, productos y ventas."
      icon={Store}
    >
      <main className="dc-stores-page">
        <button
          type="button"
          className="dc-stores-back"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={17} />
          VOLVER AL PERFIL
        </button>

        <section className="dc-stores-hero">
          <div className="dc-stores-hero-copy">
            <span className="dc-stores-eyebrow">
              LA ESQUINA DEL PARQUÉS · STORE & POS
            </span>
            <h1>
              CENTRO DE CONTROL
              <span> TIENDAS Y POS</span>
            </h1>
            <p>
              Gestiona tus tiendas, mesas, catálogo y cuentas desde una sola pantalla.
            </p>
            <div className="dc-stores-hero-actions">
              <button
                type="button"
                className="dc-stores-primary"
                onClick={startCreateStore}
                disabled={busy}
              >
                <Plus size={17} />
                NUEVA TIENDA
              </button>
              <button
                type="button"
                className="dc-stores-secondary"
                onClick={() => {
                  loadStores();
                  loadTables();
                  loadProducts();
                }}
                disabled={loading || busy}
              >
                <RefreshCw size={17} />
                ACTUALIZAR
              </button>
            </div>
          </div>
          <div className="dc-stores-hero-art">
            <div className="dc-stores-orbit one" />
            <div className="dc-stores-orbit two" />
            <div className="dc-stores-hero-icon">
              <Store size={52} strokeWidth={1.3} />
            </div>
            <span className="dc-stores-floating top">
              <BadgeCheck size={16} /> CONTROL
            </span>
            <span className="dc-stores-floating bottom">
              <CreditCard size={16} /> POS
            </span>
          </div>
        </section>

        {error && (
          <div className="dc-stores-alert error" role="alert">
            <XCircle size={19} />
            <span>{error}</span>
            <button type="button" onClick={() => setError("")} aria-label="Cerrar error">
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-stores-alert success" role="status">
            <CheckCircle2 size={19} />
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice("")} aria-label="Cerrar aviso">
              <X size={17} />
            </button>
          </div>
        )}

        <section className="dc-stores-stats">
          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon purple"><Store size={21} /></div>
            <span>TIENDAS</span>
            <strong>{totals.stores}</strong>
            <small>{totals.active} activas</small>
          </article>
          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon blue"><Building2 size={21} /></div>
            <span>MESAS</span>
            <strong>{totals.tables}</strong>
            <small>{totals.available} libres · {totals.occupied} ocupadas</small>
          </article>
          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon green"><ShoppingCart size={21} /></div>
            <span>PRODUCTOS</span>
            <strong>{totals.products}</strong>
            <small>Catálogo de la tienda seleccionada</small>
          </article>
          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon orange"><CreditCard size={21} /></div>
            <span>VENTA ACTUAL</span>
            <strong>{money(cartTotal, selectedStore?.currency)}</strong>
            <small>{cart.length} productos diferentes</small>
          </article>
        </section>

        <section className="dc-stores-panel">
          <div className="dc-stores-panel-heading">
            <div>
              <span className="dc-stores-eyebrow">OPERACIÓN</span>
              <h2>Seleccionar tienda</h2>
              <p>Las mesas y los productos se cargan según la tienda seleccionada.</p>
            </div>
            <label className="dc-stores-field">
              <span>TIENDA ACTIVA</span>
              <select
                value={selectedStoreId}
                onChange={(event) => {
                  setSelectedStoreId(event.target.value);
                  setSelectedTableId("");
                  setCart([]);
                  clearMessages();
                }}
              >
                <option value="">Selecciona una tienda</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} {store.is_active === false ? "(Inactiva)" : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {selectedStore && (
            <div className="dc-stores-detail-brand">
              <div className="dc-stores-detail-logo">
                {mediaUrl(selectedStore.logo)
                  ? <img src={mediaUrl(selectedStore.logo)} alt="" />
                  : <Store size={32} />}
              </div>
              <div>
                <h3>{selectedStore.name}</h3>
                <span>{storeAddress(selectedStore) || "Ubicación sin registrar"}</span>
              </div>
              <span className={`dc-stores-status ${selectedStore.is_active ? "active" : "inactive"}`}>
                {selectedStore.is_active ? "ACTIVA" : "INACTIVA"}
              </span>
            </div>
          )}
        </section>

        <nav className="dc-stores-tabs">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              type="button"
              key={id}
              className={tab === id ? "active" : ""}
              onClick={() => {
                setTab(id);
                clearMessages();
              }}
            >
              <Icon size={17} />
              {label.toUpperCase()}
            </button>
          ))}
        </nav>

        {loading && (
          <section className="dc-stores-panel dc-stores-loading">
            <LoaderCircle size={31} className="dc-stores-spin" />
            <strong>CARGANDO TIENDAS...</strong>
            <span>Consultando el backend.</span>
          </section>
        )}

        {/* TIENDAS */}
        {!loading && tab === "stores" && (
          <section className="dc-stores-panel">
            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">STORE DIRECTORY</span>
                <h2>Administrar tiendas</h2>
                <p>Crea, edita, desactiva o elimina registros de tiendas.</p>
              </div>
              <button
                type="button"
                className="dc-stores-primary"
                onClick={startCreateStore}
                disabled={busy}
              >
                <Plus size={17} /> NUEVA TIENDA
              </button>
            </div>

            <label className="dc-stores-search">
              <Search size={18} />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por nombre, correo, teléfono o ciudad..."
              />
            </label>

            {storeFormOpen && (
              <form className="dc-stores-form" onSubmit={saveStore}>
                <div className="dc-stores-form-section">
                  <div className="dc-stores-section-title">
                    <Building2 size={19} />
                    <div>
                      <h3>{editingStore ? "Editar tienda" : "Crear tienda"}</h3>
                      <p>Información comercial básica.</p>
                    </div>
                  </div>

                  <div className="dc-stores-form-grid">
                    <label className="dc-stores-field full">
                      <span>NOMBRE *</span>
                      <input
                        required
                        maxLength={120}
                        value={storeForm.name}
                        onChange={(event) => setStoreForm({ ...storeForm, name: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field full">
                      <span>DESCRIPCIÓN</span>
                      <textarea
                        rows={3}
                        value={storeForm.description || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, description: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>CORREO</span>
                      <input
                        type="email"
                        value={storeForm.email || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, email: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>TELÉFONO</span>
                      <input
                        value={storeForm.phone || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, phone: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>SITIO WEB</span>
                      <input
                        type="url"
                        value={storeForm.website || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, website: event.target.value })}
                        placeholder="https://..."
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>DIRECCIÓN</span>
                      <input
                        value={storeForm.address || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, address: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>CIUDAD</span>
                      <input
                        value={storeForm.city || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, city: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>DEPARTAMENTO</span>
                      <input
                        value={storeForm.state || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, state: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>MONEDA</span>
                      <select
                        value={storeForm.currency || "COP"}
                        onChange={(event) => setStoreForm({ ...storeForm, currency: event.target.value })}
                      >
                        <option value="COP">COP</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </label>
                    <label className="dc-stores-field">
                      <span>COMISIÓN (%)</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={storeForm.commission_percentage ?? "0"}
                        onChange={(event) => setStoreForm({ ...storeForm, commission_percentage: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>APERTURA</span>
                      <input
                        type="time"
                        value={storeForm.opening_time || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, opening_time: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>CIERRE</span>
                      <input
                        type="time"
                        value={storeForm.closing_time || ""}
                        onChange={(event) => setStoreForm({ ...storeForm, closing_time: event.target.value })}
                      />
                    </label>
                  </div>

                  <div className="dc-stores-toggles">
                    {[
                      ["is_active", "Tienda activa"],
                      ["is_featured", "Tienda destacada"],
                      ["is_verified", "Tienda verificada"],
                    ].map(([field, label]) => (
                      <label className="dc-stores-toggle" key={field}>
                        <input
                          type="checkbox"
                          checked={Boolean(storeForm[field])}
                          onChange={(event) => setStoreForm({
                            ...storeForm,
                            [field]: event.target.checked,
                          })}
                        />
                        <span className="dc-stores-toggle-ui" />
                        <span><strong>{label}</strong></span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="dc-stores-form-actions">
                  <button
                    type="button"
                    className="dc-stores-secondary"
                    onClick={() => setStoreFormOpen(false)}
                    disabled={busy}
                  >
                    CANCELAR
                  </button>
                  <button type="submit" className="dc-stores-primary" disabled={busy}>
                    {busy ? <LoaderCircle size={17} className="dc-stores-spin" /> : <CheckCircle2 size={17} />}
                    {editingStore ? "GUARDAR CAMBIOS" : "CREAR TIENDA"}
                  </button>
                </div>
              </form>
            )}

            <div className="dc-stores-grid">
              {filteredStores.map((store) => (
                <article className="dc-stores-card" key={store.id}>
                  <div className="dc-stores-card-banner">
                    {mediaUrl(store.banner)
                      ? <img src={mediaUrl(store.banner)} alt="" loading="lazy" />
                      : <div className="dc-stores-banner-placeholder"><Store size={35} /></div>}
                    <div className="dc-stores-card-flags">
                      <span className={`dc-stores-status ${store.is_active ? "active" : "inactive"}`}>
                        {store.is_active ? "ACTIVA" : "INACTIVA"}
                      </span>
                      {store.is_verified && <span className="dc-stores-flag verified"><BadgeCheck size={12} /> VERIFICADA</span>}
                    </div>
                  </div>

                  <div className="dc-stores-card-content">
                    <div className="dc-stores-card-brand">
                      <div className="dc-stores-logo">
                        {mediaUrl(store.logo)
                          ? <img src={mediaUrl(store.logo)} alt="" loading="lazy" />
                          : <Store size={25} />}
                      </div>
                      <div className="dc-stores-card-title">
                        <h3>{store.name}</h3>
                        <span>ID #{store.id}</span>
                      </div>
                    </div>

                    <p className="dc-stores-card-description">
                      {store.description || "Sin descripción registrada."}
                    </p>

                    <div className="dc-stores-card-meta">
                      <span><MapPin size={14} /> {storeAddress(store) || "Ubicación pendiente"}</span>
                      {store.email && <span>{store.email}</span>}
                      {store.phone && <span>{store.phone}</span>}
                    </div>

                    <div className="dc-stores-card-footer">
                      <button
                        type="button"
                        className="dc-stores-secondary"
                        onClick={() => startEditStore(store)}
                        disabled={busy}
                      >
                        <Edit3 size={15} /> EDITAR
                      </button>
                      <button
                        type="button"
                        className="dc-stores-secondary"
                        onClick={() => toggleStoreActive(store)}
                        disabled={busy}
                      >
                        <Eye size={15} /> {store.is_active ? "DESACTIVAR" : "ACTIVAR"}
                      </button>
                    </div>

                    <div className="dc-stores-quick-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStoreId(String(store.id));
                          setTab("tables");
                        }}
                      >
                        <Building2 size={14} /> VER MESAS
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStoreId(String(store.id));
                          setTab("pos");
                        }}
                      >
                        <CreditCard size={14} /> ABRIR POS
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStore(store)}
                        disabled={busy}
                      >
                        <Trash2 size={14} /> ELIMINAR
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {!filteredStores.length && (
              <div className="dc-stores-empty">
                <Store size={40} />
                <strong>NO HAY TIENDAS PARA MOSTRAR</strong>
                <span>Registra una tienda o cambia la búsqueda.</span>
              </div>
            )}
          </section>
        )}

        {/* MESAS */}
        {!loading && tab === "tables" && (
          <section className="dc-stores-panel">
            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">TABLE MANAGEMENT</span>
                <h2>Mesas de {selectedStore?.name || "la tienda"}</h2>
                <p>Administra las mesas y su estado operativo.</p>
              </div>
              <button
                type="button"
                className="dc-stores-primary"
                onClick={startCreateTable}
                disabled={!selectedStoreId || busy}
              >
                <Plus size={17} /> NUEVA MESA
              </button>
            </div>

            {tableFormOpen && (
              <form className="dc-stores-form" onSubmit={saveTable}>
                <div className="dc-stores-form-section">
                  <div className="dc-stores-form-grid">
                    <label className="dc-stores-field">
                      <span>NOMBRE</span>
                      <input
                        value={tableForm.name}
                        onChange={(event) => setTableForm({ ...tableForm, name: event.target.value })}
                        placeholder="Mesa terraza"
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>NÚMERO</span>
                      <input
                        value={tableForm.number}
                        onChange={(event) => setTableForm({ ...tableForm, number: event.target.value })}
                        placeholder="1"
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>CAPACIDAD</span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={tableForm.capacity}
                        onChange={(event) => setTableForm({ ...tableForm, capacity: event.target.value })}
                      />
                    </label>
                    <label className="dc-stores-field">
                      <span>ESTADO</span>
                      <select
                        value={tableForm.status}
                        onChange={(event) => setTableForm({ ...tableForm, status: event.target.value })}
                      >
                        {TABLE_STATES.map((state) => (
                          <option key={state.value} value={state.value}>{state.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="dc-stores-field full">
                      <span>NOTAS</span>
                      <textarea
                        rows={2}
                        value={tableForm.notes || ""}
                        onChange={(event) => setTableForm({ ...tableForm, notes: event.target.value })}
                      />
                    </label>
                  </div>
                </div>
                <div className="dc-stores-form-actions">
                  <button type="button" className="dc-stores-secondary" onClick={() => setTableFormOpen(false)} disabled={busy}>
                    CANCELAR
                  </button>
                  <button type="submit" className="dc-stores-primary" disabled={busy}>
                    <CheckCircle2 size={17} /> {editingTable ? "GUARDAR MESA" : "CREAR MESA"}
                  </button>
                </div>
              </form>
            )}

            {!selectedStoreId ? (
              <div className="dc-stores-empty">Selecciona una tienda primero.</div>
            ) : (
              <div className="dc-stores-grid">
                {tables.map((table) => (
                  <article className="dc-stores-card" key={table.id}>
                    <div className="dc-stores-card-content">
                      <div className="dc-stores-card-brand">
                        <div className="dc-stores-logo"><Building2 size={25} /></div>
                        <div className="dc-stores-card-title">
                          <h3>{table.name || `Mesa ${table.number}`}</h3>
                          <span>Mesa #{table.number ?? table.id}</span>
                        </div>
                      </div>
                      <p className="dc-stores-card-description">
                        Capacidad: {table.capacity ?? 4} personas
                      </p>
                      <span className={`dc-stores-status ${table.status === "available" ? "active" : "inactive"}`}>
                        {tableStateLabel(table.status)}
                      </span>

                      <label className="dc-stores-field">
                        <span>CAMBIAR ESTADO</span>
                        <select
                          value={table.status || "available"}
                          disabled={busy}
                          onChange={(event) => updateTableStatus(table, event.target.value)}
                        >
                          {TABLE_STATES.map((state) => (
                            <option key={state.value} value={state.value}>{state.label}</option>
                          ))}
                        </select>
                      </label>

                      <div className="dc-stores-card-footer">
                        <button
                          type="button"
                          className="dc-stores-secondary"
                          onClick={() => startEditTable(table)}
                          disabled={busy}
                        >
                          <Edit3 size={15} /> EDITAR
                        </button>
                        <button
                          type="button"
                          className="dc-stores-primary"
                          onClick={() => {
                            setSelectedTableId(String(table.id));
                            setTab("pos");
                          }}
                          disabled={table.status === "inactive" || table.status === "reserved"}
                        >
                          <ShoppingCart size={15} /> VENDER
                        </button>
                      </div>
                      <div className="dc-stores-quick-actions">
                        <button type="button" onClick={() => deleteTable(table)} disabled={busy}>
                          <Trash2 size={14} /> ELIMINAR MESA
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {selectedStoreId && tables.length === 0 && (
              <div className="dc-stores-empty">
                <Building2 size={40} />
                <strong>NO HAY MESAS REGISTRADAS</strong>
                <span>Crea las mesas desde este panel.</span>
              </div>
            )}
          </section>
        )}

        {/* CATÁLOGO */}
        {!loading && tab === "products" && (
          <section className="dc-stores-panel">
            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">PRODUCT CATALOG</span>
                <h2>Productos de {selectedStore?.name || "la tienda"}</h2>
                <p>Selecciona productos para incorporarlos a una cuenta en el POS.</p>
              </div>
              <button
                type="button"
                className="dc-stores-secondary"
                onClick={loadProducts}
                disabled={busy}
              >
                <RefreshCw size={16} /> ACTUALIZAR
              </button>
            </div>

            <label className="dc-stores-search">
              <Search size={18} />
              <input
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Buscar producto, SKU o código de barras..."
              />
            </label>

            <div className="dc-stores-grid">
              {filteredProducts.map((product) => (
                <article className="dc-stores-card" key={product.id}>
                  <div className="dc-stores-card-banner">
                    {mediaUrl(product.image || product.original_image)
                      ? <img src={mediaUrl(product.image || product.original_image)} alt="" loading="lazy" />
                      : <div className="dc-stores-banner-placeholder"><ShoppingCart size={35} /></div>}
                  </div>
                  <div className="dc-stores-card-content">
                    <div className="dc-stores-card-title">
                      <h3>{product.name}</h3>
                      <span>SKU: {product.sku || "Sin SKU"}</span>
                    </div>
                    <p className="dc-stores-card-description">
                      {product.description || "Sin descripción."}
                    </p>
                    <div className="dc-stores-card-metrics">
                      <div>
                        <span>PRECIO</span>
                        <strong>{money(getProductPrice(product), selectedStore?.currency)}</strong>
                      </div>
                      <div>
                        <span>STOCK</span>
                        <strong>{product.stock ?? product.available_stock ?? "—"}</strong>
                      </div>
                    </div>
                    <div className="dc-stores-card-footer">
                      <button
                        type="button"
                        className="dc-stores-primary"
                        onClick={() => addToCart(product)}
                        disabled={!selectedStoreId || !selectedTableId || busy}
                      >
                        <Plus size={16} /> AGREGAR A VENTA
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {!filteredProducts.length && (
              <div className="dc-stores-empty">
                <ShoppingCart size={40} />
                <strong>NO HAY PRODUCTOS DISPONIBLES</strong>
                <span>Comprueba que existan productos asociados a la tienda y que la API los devuelva.</span>
              </div>
            )}
          </section>
        )}

        {/* PUNTO DE VENTA */}
        {!loading && tab === "pos" && (
          <section className="dc-stores-panel">
            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">POINT OF SALE</span>
                <h2>Punto de venta</h2>
                <p>Selecciona una mesa, agrega productos y registra la cuenta.</p>
              </div>
              <span className="dc-stores-count">{cart.length} LÍNEAS</span>
            </div>

            <div className="dc-stores-form-grid">
              <label className="dc-stores-field">
                <span>TIENDA</span>
                <select
                  value={selectedStoreId}
                  onChange={(event) => {
                    setSelectedStoreId(event.target.value);
                    setSelectedTableId("");
                    setCart([]);
                  }}
                >
                  <option value="">Selecciona tienda</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </label>

              <label className="dc-stores-field">
                <span>MESA</span>
                <select
                  value={selectedTableId}
                  onChange={(event) => setSelectedTableId(event.target.value)}
                  disabled={!selectedStoreId}
                >
                  <option value="">Selecciona mesa</option>
                  {tables
                    .filter((table) => !["inactive", "reserved"].includes(table.status))
                    .map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.name || `Mesa ${table.number}`} · {tableStateLabel(table.status)}
                      </option>
                    ))}
                </select>
              </label>

              <label className="dc-stores-field">
                <span>NOMBRE DEL CLIENTE (OPCIONAL)</span>
                <input
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  placeholder="Nombre del cliente"
                />
              </label>

              <label className="dc-stores-field">
                <span>MÉTODO DE PAGO</span>
                <select
                  value={paymentMethod}
                  onChange={(event) => setPaymentMethod(event.target.value)}
                >
                  <option value="cash">Efectivo</option>
                  <option value="card">Tarjeta</option>
                  <option value="transfer">Transferencia</option>
                  <option value="pending">Cuenta pendiente</option>
                </select>
              </label>
            </div>

            <div className="dc-stores-panel-heading">
              <div>
                <h3>Agregar productos</h3>
                <p>Elige un producto del catálogo de la tienda seleccionada.</p>
              </div>
              <button
                type="button"
                className="dc-stores-secondary"
                onClick={() => setTab("products")}
              >
                <ShoppingCart size={16} /> VER CATÁLOGO
              </button>
            </div>

            <label className="dc-stores-search">
              <Search size={18} />
              <input
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Buscar producto..."
              />
            </label>

            <div className="dc-stores-pos-products">
              {filteredProducts.map((product) => (
                <button
                  type="button"
                  className="dc-stores-pos-product"
                  key={product.id}
                  onClick={() => addToCart(product)}
                  disabled={!selectedTableId || busy}
                >
                  <span>{product.name}</span>
                  <strong>{money(getProductPrice(product), selectedStore?.currency)}</strong>
                  <Plus size={16} />
                </button>
              ))}
            </div>

            <div className="dc-stores-detail-section">
              <h3><ShoppingCart size={18} /> Cuenta de la mesa</h3>

              {!selectedTableId && (
                <p>Selecciona una mesa para iniciar la cuenta.</p>
              )}

              {cart.length === 0 ? (
                <div className="dc-stores-empty">
                  <ShoppingCart size={35} />
                  <strong>CUENTA VACÍA</strong>
                  <span>Agrega productos para calcular el total.</span>
                </div>
              ) : (
                <div className="dc-stores-pos-cart">
                  {cart.map((item) => (
                    <div className="dc-stores-pos-cart-row" key={item.id}>
                      <div>
                        <strong>{item.name}</strong>
                        <span>{money(item.price, selectedStore?.currency)} por unidad</span>
                      </div>
                      <div className="dc-stores-pos-quantity">
                        <button
                          type="button"
                          onClick={() => changeCartQuantity(item.id, -1)}
                          aria-label={`Quitar una unidad de ${item.name}`}
                        >
                          <Minus size={15} />
                        </button>
                        <strong>{item.quantity}</strong>
                        <button
                          type="button"
                          onClick={() => changeCartQuantity(item.id, 1)}
                          aria-label={`Agregar una unidad de ${item.name}`}
                        >
                          <Plus size={15} />
                        </button>
                      </div>
                      <strong>{money(item.price * item.quantity, selectedStore?.currency)}</strong>
                    </div>
                  ))}
                </div>
              )}

              <label className="dc-stores-field">
                <span>NOTAS DE LA CUENTA</span>
                <textarea
                  rows={2}
                  value={customerNotes}
                  onChange={(event) => setCustomerNotes(event.target.value)}
                  placeholder="Observaciones, pedidos especiales..."
                />
              </label>

              <div className="dc-stores-pos-total">
                <span>TOTAL</span>
                <strong>{money(cartTotal, selectedStore?.currency)}</strong>
              </div>

              <div className="dc-stores-form-actions">
                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() => setCart([])}
                  disabled={!cart.length || busy}
                >
                  <Trash2 size={16} /> VACIAR CUENTA
                </button>
                <button
                  type="button"
                  className="dc-stores-primary"
                  onClick={submitSale}
                  disabled={!selectedStoreId || !selectedTableId || !cart.length || busy}
                >
                  {busy
                    ? <LoaderCircle size={17} className="dc-stores-spin" />
                    : <CreditCard size={17} />}
                  {busy ? "REGISTRANDO..." : "REGISTRAR VENTA"}
                </button>
              </div>

              <div className="dc-stores-inline-note">
                <ShieldCheck size={17} />
                <span>
                  El backend debe recalcular los precios, validar el inventario,
                  guardar los ítems y gestionar el pago dentro de una transacción.
                  El total mostrado en pantalla es una estimación previa.
                </span>
              </div>
            </div>
          </section>
        )}

        <footer className="dc-stores-footer">
          <span><ShieldCheck size={16} /> LA ESQUINA DEL PARQUÉS · STORE & POS</span>
          <span>PERMISOS, INVENTARIO, PAGOS Y VENTAS SE VALIDAN EN DJANGO</span>
        </footer>
      </main>
    </ProfilePageLayout>
  );
}
